import { generatePath } from 'react-router-dom'
import { Tooltip, Typography } from 'antd'
import moment from 'moment'
import Countdown, { zeroPad } from 'react-countdown'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

import { Assignee } from 'admin/lib/Assignee'
import { EditLink } from 'admin/lib/Common/StyledElements'
import { Flag } from 'admin/lib/Icons/Flag'
import * as ROUTES from 'admin/routes'
import { TemplateNameIndicator } from 'admin/pages/RecordEdit/common'

/** @type {import("antd/lib/table").ColumnsType<any>} */

const addBusinessDay = timestamp => {
  const date = moment(timestamp)
  const dayOfWeek = date.day()

  // If Friday (5), add 3 days to get to Monday
  if (dayOfWeek === 5) {
    return date.add(3, 'days')
  }
  // If Saturday (6), add 2 days to get to Monday
  if (dayOfWeek === 6) {
    return date.add(2, 'days')
  }
  // If Sunday (0), add 1 day to get to Monday
  if (dayOfWeek === 0) {
    return date.add(1, 'day')
  }
  // Otherwise, just add 1 day (Mon-Thu)
  return date.add(1, 'day')
}

const getBusinessHoursUntilTarget = (startDate, targetDate) => {
  const now = moment(startDate)
  const target = moment(targetDate)
  let hours = 0

  // Count hours, skipping weekends
  while (now.isBefore(target)) {
    const dayOfWeek = now.day()
    // Only count hours if not weekend (0 = Sunday, 6 = Saturday)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      hours++
    }
    now.add(1, 'hour')
  }

  return hours
}

const CountdownElement = (timestamp, assignee, status) => {
  if (
    !timestamp ||
    !status ||
    status === 'Removed' ||
    status === 'Published' ||
    status === 'AutoPublished' ||
    status === 'Void'
  ) {
    return false
  }
  const isBackDated = assignee === 'backdated'

  const targetDate = addBusinessDay(timestamp)
  const hoursUntilTarget = getBusinessHoursUntilTarget(moment(), targetDate)

  // Only show countdown if less than 24 business hours remain
  if (hoursUntilTarget >= 24 || isBackDated) {
    return false
  }

  return (
    <Countdown
      date={targetDate.toDate()}
      renderer={({ hours, minutes, completed }) => {
        if (completed) {
          return (
            <span style={{ color: colours.RED_DARKMODE }}>SLA Expired</span>
          )
        } else {
          return (
            <span
              style={
                hoursUntilTarget < 4 ? { color: colours.ORANGE_DARKMODE } : {}
              }
            >
              {zeroPad(hours)}:{zeroPad(minutes)}
            </span>
          )
        }
      }}
    />
  )
}

export const columns = [
  {
    title: 'Creation date',
    dataIndex: 'timestamp',
    width: '7.25rem',
    render: (timestamp, record) => {
      return (
        <Tooltip
          title={
            record.movementDateTime ? (
              <span>
                Movement date:
                <br />
                {new Date(record.movementDateTime).toLocaleString([], {
                  timeZoneName: 'short',
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric'
                })}
              </span>
            ) : (
              ''
            )
          }
        >
          <StyledDateText>
            {new Date(timestamp).toLocaleString([], {
              timeZoneName: 'short',
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric'
            })}
          </StyledDateText>
          <br />
          {CountdownElement(timestamp, record.assignee, record.status)}
        </Tooltip>
      )
    }
  },
  { title: 'Project', dataIndex: ['project', 'name'] },
  {
    title: 'Record type',
    dataIndex: 'moduleName',
    width: '6.375rem',
    render: moduleName => {
      return (
        <Typography.Text data-test-id="moduleNameText">
          {moduleName}
        </Typography.Text>
      )
    }
  },
  {
    title: 'Template',
    dataIndex: 'templateName',
    render: (value, record) => {
      if (value) {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <TemplateNameIndicator
              estimatedAccuracy={record.estimatedAccuracy}
            />
            {value}
          </div>
        )
      }
      return ''
    }
  },
  { title: 'Movement ID', dataIndex: 'movementId', width: '12rem' },
  { title: 'Supplier / Carrier', dataIndex: 'companyName' },
  { title: 'Status', dataIndex: 'status', width: '6rem' },
  {
    title: 'Assignee',
    dataIndex: 'assignee',
    align: 'center',
    width: '6.125rem',
    render: value => {
      if (value) {
        return <Assignee fullname={value} />
      }
      return ''
    }
  },
  {
    title: 'Link',
    width: '10rem',
    render: (value, record) => {
      return (
        <Tooltip
          title={
            record?.possibleDuplicateRecordsCount > 0
              ? 'Possible duplicate'
              : ''
          }
        >
          <div
            className={
              record.possibleDuplicateRecordsCount > 0
                ? 'possible-duplicate-wrapper warning-no-icon'
                : ''
            }
          >
            {record.accounts &&
              record.accounts.map(account => {
                return (
                  <EditLink
                    key={account.accountId}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: 'normal' }}
                    href={generatePath(ROUTES.RECORD.EDIT, {
                      accountId: account.accountId,
                      projectId: record.project.projectId,
                      moduleName: record.moduleName,
                      recordId: record.recordId
                    })}
                  >
                    {account.name}: Edit
                  </EditLink>
                )
              })}
          </div>
        </Tooltip>
      )
    }
  },
  {
    title: 'Pages',
    dataIndex: 'attachmentPages',
    align: 'center',
    width: '4.875rem',
    render: (value, record) => {
      if (record.status !== 'New') {
        const pages = value ? value : 1

        if (pages > 9) {
          return <div className="page-icon page-icon-red">{pages}</div>
        }
        if (pages > 2) {
          return <div className="page-icon page-icon-amber">{pages}</div>
        }
        if (pages > 1) {
          return <div className="page-icon">{pages}</div>
        }
        if (pages === -1) {
          return (
            <div className="page-icon page-icon-red page-icon-error">!</div>
          )
        }
      }
      return <span>&nbsp;</span>
    }
  },
  {
    title: 'Country',
    dataIndex: ['project', 'country'],
    align: 'center',
    width: '5.75rem',
    render: (value, record) => {
      if (record?.project?.country) {
        return <Flag country={record.project.country} />
      }
      return ''
    }
  }
]

const StyledDateText = styled(Typography.Text)`
  &.SLAExpired {
    color: ${colours.RED_DARKMODE} !important;
  }
`
